import React, { useMemo, useState } from "react";
import {
  Autocomplete, Box, Button, Grid, IconButton, TextField, Typography,
  Divider, CircularProgress, alpha, createFilterOptions,
  Radio, RadioGroup, FormControlLabel, Chip, Alert,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import TvIcon from "@mui/icons-material/Tv";
import AddIcon from "@mui/icons-material/Add";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SettingsIcon from "@mui/icons-material/Settings";
import PlaylistPlayIcon from "@mui/icons-material/PlaylistPlay";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import { useNavigate } from "react-router-dom";
import {
  useCreateDeviceMutation,
  useClaimDevicePairingMutation,
  useResendDeviceActivationMutation,
  useChangeDeviceActivationEmailMutation,
  useGetPlaylistGroupsQuery,
  useGetCompanyQuery,
} from "../../../Api/attoDeskApi";
import { IPlaylistGroup } from "../../../Api/Interface/api.interface";
import { useNotifier } from "../../../Core/Notifier";
import SignageSection, {
  signageLabelSx, signageInputSx,
} from "../../../Components/ui/SignageSection";
import PlaylistGroupSelect from "../../../Components/ui/PlaylistGroupSelect";
import { useAppSelector } from "../../../Store/hooks";
import {
  selectIsSuperAdmin, selectCurrentUser, selectCanCreateCompany,
} from "../../../Store/Auth/AuthSelector";
import CompanyQuickCreateDialog from "../../CompanyInfo/CompanyQuickCreateDialog";

type ActivationMethod = "email" | "later" | "tv_pair";
type Recipient = "company_email" | "my_account" | "custom";

interface CreatedInfo {
  deviceId: number;
  publicReference: string;
  deviceName: string;
  method: ActivationMethod;
  sentTo?: string | null;      // masked email, e.g. j***@example.com
  expiresAt?: string | null;   // ISO
  ttlMinutes?: number | null;
}

const DeviceCreate: React.FC = () => {
  const navigate = useNavigate();
  const { showMessage, showErrorMessage } = useNotifier();
  const [createDevice, { isLoading }] = useCreateDeviceMutation();
  const [claimPairing] = useClaimDevicePairingMutation();
  const [resendActivation, { isLoading: isResending }] = useResendDeviceActivationMutation();
  const [changeEmail, { isLoading: isChangingEmail }] = useChangeDeviceActivationEmailMutation();
  const { data: groupsData } = useGetPlaylistGroupsQuery();

  const groups: IPlaylistGroup[] = (Array.isArray(groupsData?.data)
    ? ((groupsData?.data as unknown) as IPlaylistGroup[])
    : []).filter(g => g.is_active);

  const [deviceName, setDeviceName] = useState("");
  const [location, setLocation] = useState("");
  const [notes, setNotes] = useState("");
  const [playlistGroupId, setPlaylistGroupId] = useState<number | "">("");
  const [companyId, setCompanyId] = useState<number | "">("");

  // Activation
  const [activationMethod, setActivationMethod] = useState<ActivationMethod>("email");
  const [recipient, setRecipient] = useState<Recipient>("company_email");
  const [activationEmail, setActivationEmail] = useState("");
  const [activationEmailConfirm, setActivationEmailConfirm] = useState("");
  const [pairingCode, setPairingCode] = useState("");

  // Post-save state (shown in place of the form once the device is created).
  const [createdInfo, setCreatedInfo] = useState<CreatedInfo | null>(null);
  const [changeEmailMode, setChangeEmailMode] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newEmailConfirm, setNewEmailConfirm] = useState("");

  const isSuperAdmin = useAppSelector(selectIsSuperAdmin);
  const currentUser = useAppSelector(selectCurrentUser);
  const canCreateCompany = useAppSelector(selectCanCreateCompany);
  const { data: companiesData, refetch: refetchCompanies } = useGetCompanyQuery(
    { searchText: "", id: "" } as any,
    { skip: !isSuperAdmin }
  );
  const companies: Array<{ id: number; companyName: string }> = Array.isArray(
    (companiesData as any)?.data
  )
    ? ((companiesData as any).data as Array<{ id: number; companyName: string }>)
    : [];

  const ADD_COMPANY_SENTINEL_ID = -1;
  const [companyPickerInput, setCompanyPickerInput] = useState("");
  const [addCompanyOpen, setAddCompanyOpen] = useState(false);
  const companyFilter = useMemo(
    () => createFilterOptions<{ id: number; companyName: string }>({
      trim: true, ignoreCase: true, matchFrom: "any",
      stringify: (o) => o.companyName,
    }),
    []
  );
  const selectedCompany =
    companyId === "" ? null : (companies.find((c) => c.id === companyId) ?? null);

  const emailFieldsValid = () => {
    if (activationMethod !== "email" || recipient !== "custom") return true;
    const a = activationEmail.trim();
    return a.length > 0 && /\S+@\S+\.\S+/.test(a) && a === activationEmailConfirm.trim();
  };

  const isValid =
    deviceName.trim().length > 0 &&
    (!isSuperAdmin || companyId !== "") &&
    (activationMethod !== "tv_pair" || pairingCode.trim().length >= 4) &&
    emailFieldsValid();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (isSubmitting || isLoading) return;
    if (!isValid) {
      if (isSuperAdmin && companyId === "") showErrorMessage("Please pick a company");
      else if (deviceName.trim().length === 0) showErrorMessage("Device name is required");
      else if (activationMethod === "tv_pair") showErrorMessage("Enter the code shown on the TV");
      else showErrorMessage("Please enter and confirm a valid activation email");
      return;
    }
    setIsSubmitting(true);
    try {
      const base: any = {
        device_name: deviceName.trim(),
        location: location.trim() || null,
        playlist_group_id: playlistGroupId === "" ? null : Number(playlistGroupId),
      };
      if (isSuperAdmin) base.company_id = Number(companyId);

      if (activationMethod === "tv_pair") {
        const res = await claimPairing({ ...base, pairing_code: pairingCode.trim() }).unwrap();
        if (res.status) {
          const id = (res as any).data?.id;
          showMessage("Screen paired successfully");
          if (id != null) { navigate(`/device/view/${id}`); return; }
        } else {
          showErrorMessage((res as any).message || "Could not pair the screen");
        }
        setIsSubmitting(false);
        return;
      }

      // email | later
      const payload: any = { ...base, activation_method: activationMethod };
      if (activationMethod === "email") {
        payload.activation_recipient = recipient;
        if (recipient === "custom") {
          payload.activation_email = activationEmail.trim();
          payload.activation_email_confirmation = activationEmailConfirm.trim();
        }
      }
      const res = await createDevice(payload).unwrap();
      if (res.status) {
        const d = (res as any).data;
        const act = (res as any).activation ?? {};
        showMessage(act.sent_to ? `Device created — code sent to ${act.sent_to}` : "Device created");
        setCreatedInfo({
          deviceId: d?.id,
          publicReference: d?.public_reference,
          deviceName: d?.device_name ?? deviceName.trim(),
          method: (act.method as ActivationMethod) ?? activationMethod,
          sentTo: act.sent_to ?? null,
          expiresAt: act.expires_at ?? null,
          ttlMinutes: act.ttl_minutes ?? null,
        });
      } else {
        showErrorMessage((res as any).message || "Failed to create device");
      }
    } catch (err: any) {
      showErrorMessage(err?.data?.message || "Failed to create device");
    }
    setIsSubmitting(false);
  };

  const handleResend = async () => {
    if (!createdInfo) return;
    try {
      const res = await resendActivation({ id: createdInfo.deviceId }).unwrap();
      if (res.status) {
        const d = (res as any).data ?? {};
        setCreatedInfo({ ...createdInfo, sentTo: d.sent_to, expiresAt: d.expires_at, ttlMinutes: d.ttl_minutes, method: "email" });
        showMessage(`Activation code re-sent to ${d.sent_to}`);
      } else showErrorMessage((res as any).message || "Could not resend the code");
    } catch (e: any) { showErrorMessage(e?.data?.message || "Could not resend the code"); }
  };

  const handleChangeEmail = async () => {
    if (!createdInfo) return;
    if (!/\S+@\S+\.\S+/.test(newEmail.trim()) || newEmail.trim() !== newEmailConfirm.trim()) {
      showErrorMessage("Enter and confirm a valid email");
      return;
    }
    try {
      const res = await changeEmail({
        id: createdInfo.deviceId,
        email: newEmail.trim(),
        email_confirmation: newEmailConfirm.trim(),
      }).unwrap();
      if (res.status) {
        const d = (res as any).data ?? {};
        setCreatedInfo({ ...createdInfo, sentTo: d.sent_to, expiresAt: d.expires_at, ttlMinutes: d.ttl_minutes, method: "email" });
        setChangeEmailMode(false); setNewEmail(""); setNewEmailConfirm("");
        showMessage(`Activation code sent to ${d.sent_to}`);
      } else showErrorMessage((res as any).message || "Could not update the email");
    } catch (e: any) { showErrorMessage(e?.data?.message || "Could not update the email"); }
  };

  const CARD = "#00838f";

  return (
    <Box sx={{ bgcolor: "#f1f5f9", minHeight: "calc(100vh - 100px)", mx: -3, mt: -3 }}>
      {/* ── Top Bar ── */}
      <Box sx={{
        background: "linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)",
        px: 3, py: 0, position: "sticky", top: 0, zIndex: 10,
        boxShadow: "0 4px 30px rgba(0,0,0,0.15)",
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 2 }}>
          <IconButton
            onClick={() => navigate("/device/list")} size="small"
            sx={{
              border: "1px solid rgba(255,255,255,0.15)", borderRadius: 2,
              width: 38, height: 38, backdropFilter: "blur(10px)",
              bgcolor: "rgba(255,255,255,0.05)",
              "&:hover": { bgcolor: "rgba(255,255,255,0.12)", borderColor: "rgba(255,255,255,0.3)" },
            }}
          >
            <ArrowBackIcon sx={{ fontSize: 18, color: "#e2e8f0" }} />
          </IconButton>
          <Divider orientation="vertical" flexItem sx={{ borderColor: "rgba(255,255,255,0.1)" }} />
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box sx={{
                width: 32, height: 32, borderRadius: 1.5,
                display: "flex", alignItems: "center", justifyContent: "center",
                background: "linear-gradient(135deg, #00838f 0%, #00acc1 100%)",
                boxShadow: "0 2px 8px rgba(0,131,143,0.4)",
              }}>
                <TvIcon sx={{ fontSize: 16, color: "#fff" }} />
              </Box>
              <Typography fontSize={18} fontWeight={800} color="#fff" letterSpacing="-0.02em">
                New Device
              </Typography>
            </Box>
            <Typography fontSize={11} color="rgba(255,255,255,0.45)" mt={0.25} ml={5.5}>
              {createdInfo ? "Screen registered — pending activation" : "Fill in the details to register a new screen"}
            </Typography>
          </Box>
          {!createdInfo && (
            <>
              <Button
                variant="outlined" size="small"
                onClick={() => navigate("/device/list")}
                sx={{
                  borderRadius: 2.5, textTransform: "none", fontSize: 12, fontWeight: 600,
                  borderColor: "rgba(255,255,255,0.15)", color: "#94a3b8", px: 2.5,
                  "&:hover": { borderColor: "rgba(255,255,255,0.3)", bgcolor: "rgba(255,255,255,0.05)", color: "#e2e8f0" },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained" disableElevation size="small"
                disabled={!isValid || isLoading || isSubmitting} onClick={handleSubmit}
                startIcon={
                  isLoading || isSubmitting
                    ? <CircularProgress size={14} sx={{ color: "#fff" }} />
                    : <SaveIcon sx={{ fontSize: 16 }} />
                }
                sx={{
                  borderRadius: 2.5, textTransform: "none", fontSize: 12, fontWeight: 700, px: 3,
                  background: "linear-gradient(135deg, #00838f 0%, #00acc1 100%)",
                  boxShadow: "0 2px 12px rgba(0,131,143,0.4)",
                  "&:hover": { background: "linear-gradient(135deg, #006064 0%, #00838f 100%)" },
                  "&.Mui-disabled": { background: "rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.25)" },
                }}
              >
                {isLoading || isSubmitting ? "Saving..." : (activationMethod === "tv_pair" ? "Pair Screen" : "Save Device")}
              </Button>
            </>
          )}
          {createdInfo && (
            <Button
              variant="contained" disableElevation size="small"
              onClick={() => navigate(`/device/view/${createdInfo.deviceId}`)}
              startIcon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
              sx={{
                borderRadius: 2.5, textTransform: "none", fontSize: 12, fontWeight: 700, px: 3,
                background: "linear-gradient(135deg, #00838f 0%, #00acc1 100%)",
                "&:hover": { background: "linear-gradient(135deg, #006064 0%, #00838f 100%)" },
              }}
            >
              Done
            </Button>
          )}
        </Box>
      </Box>

      {/* ── Content ── */}
      <Box sx={{ p: 3 }}>
        {createdInfo ? (
          /* ─────────── Post-save: pending activation summary ─────────── */
          <Box sx={{ maxWidth: 640, mx: "auto" }}>
            <SignageSection
              icon={<ShieldOutlinedIcon sx={{ fontSize: 18, color: CARD }} />}
              title="Pending Activation"
              accentColor={CARD}
              subtitle="The screen stays inactive until the one-time code is entered"
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2 }}>
                <Chip size="small" label="Pending activation"
                  sx={{ bgcolor: alpha("#f59e0b", 0.14), color: "#b45309", fontWeight: 700, height: 24 }} />
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>{createdInfo.deviceName}</Typography>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "space-between", py: 1.25, borderTop: "1px solid #eef2f7" }}>
                <Typography sx={{ fontSize: 12, color: "#64748b" }}>Device ID</Typography>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#0f172a", fontFamily: "monospace" }}>
                  {createdInfo.publicReference}
                </Typography>
              </Box>

              {createdInfo.method === "email" && createdInfo.sentTo && (
                <>
                  <Box sx={{ display: "flex", justifyContent: "space-between", py: 1.25, borderTop: "1px solid #eef2f7" }}>
                    <Typography sx={{ fontSize: 12, color: "#64748b" }}>Activation code sent to</Typography>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#0f172a" }}>{createdInfo.sentTo}</Typography>
                  </Box>
                  {createdInfo.ttlMinutes && (
                    <Box sx={{ display: "flex", justifyContent: "space-between", py: 1.25, borderTop: "1px solid #eef2f7" }}>
                      <Typography sx={{ fontSize: 12, color: "#64748b" }}>Expires</Typography>
                      <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#0f172a" }}>
                        in {createdInfo.ttlMinutes} minutes
                      </Typography>
                    </Box>
                  )}
                </>
              )}

              {createdInfo.method !== "email" && (
                <Alert severity="info" sx={{ mt: 2, fontSize: 12.5, borderRadius: 2 }}>
                  No activation code has been sent yet. Use <strong>Send code</strong> below when the screen is ready.
                </Alert>
              )}

              <Alert severity="success" icon={<MarkEmailReadOutlinedIcon fontSize="inherit" />} sx={{ mt: 2, fontSize: 12.5, borderRadius: 2 }}>
                For security, the full activation code is only delivered by email and is never shown here. The permanent device credential is never emailed.
              </Alert>

              {/* Actions */}
              {!changeEmailMode ? (
                <Box sx={{ display: "flex", gap: 1, mt: 2, flexWrap: "wrap" }}>
                  <Button size="small" variant="outlined" onClick={handleResend} disabled={isResending}
                    startIcon={isResending ? <CircularProgress size={14} /> : <MarkEmailReadOutlinedIcon sx={{ fontSize: 16 }} />}
                    sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600 }}>
                    {createdInfo.sentTo ? "Resend code" : "Send code"}
                  </Button>
                  <Button size="small" variant="text" onClick={() => setChangeEmailMode(true)}
                    sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600, color: CARD }}>
                    Change email
                  </Button>
                </Box>
              ) : (
                <Box sx={{ mt: 2, p: 2, border: "1px solid #e2e8f0", borderRadius: 2, bgcolor: "#f8fafc" }}>
                  <Typography sx={{ ...signageLabelSx }}>New activation email</Typography>
                  <TextField fullWidth size="small" type="email" value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)} sx={{ ...signageInputSx, mb: 1.5 }} />
                  <Typography sx={{ ...signageLabelSx }}>Confirm email</Typography>
                  <TextField fullWidth size="small" type="email" value={newEmailConfirm}
                    onChange={(e) => setNewEmailConfirm(e.target.value)} sx={{ ...signageInputSx, mb: 1.5 }} />
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button size="small" variant="contained" disableElevation onClick={handleChangeEmail} disabled={isChangingEmail}
                      sx={{ textTransform: "none", borderRadius: 2, fontWeight: 700, background: `linear-gradient(135deg, ${CARD} 0%, #00acc1 100%)` }}>
                      Send to new email
                    </Button>
                    <Button size="small" variant="text" onClick={() => setChangeEmailMode(false)}
                      sx={{ textTransform: "none", borderRadius: 2 }}>Cancel</Button>
                  </Box>
                </Box>
              )}
            </SignageSection>
          </Box>
        ) : (
        <Grid container spacing={3}>
          {/* LEFT COLUMN */}
          <Grid item xs={12} lg={8}>
            <SignageSection
              icon={<InfoOutlinedIcon sx={{ fontSize: 18, color: "#3b82f6" }} />}
              title="Device Information"
              badge="Required"
              accentColor="#3b82f6"
              subtitle="Device name, location, and notes"
            >
              <Grid container spacing={2.5}>
                {isSuperAdmin && (
                  <Grid item xs={12}>
                    <Typography sx={signageLabelSx}>Company *</Typography>
                    <Autocomplete<{ id: number; companyName: string }, false, false, false>
                      size="small"
                      options={companies}
                      value={selectedCompany}
                      inputValue={companyPickerInput}
                      onInputChange={(_, v, reason) => {
                        if (reason === "reset") return;
                        setCompanyPickerInput(v);
                      }}
                      onChange={(_, option) => {
                        if (!option) { setCompanyId(""); setCompanyPickerInput(""); return; }
                        if (option.id === ADD_COMPANY_SENTINEL_ID) { setAddCompanyOpen(true); return; }
                        setCompanyId(option.id);
                        setCompanyPickerInput(option.companyName);
                      }}
                      getOptionLabel={(o) => o.companyName || ""}
                      isOptionEqualToValue={(a, b) => a.id === b.id}
                      filterSelectedOptions={false}
                      filterOptions={(opts, params) => {
                        const filtered = companyFilter(opts, params);
                        if (canCreateCompany) {
                          filtered.push({ id: ADD_COMPANY_SENTINEL_ID, companyName: "__ADD_NEW_COMPANY__" });
                        }
                        return filtered;
                      }}
                      renderOption={(props, option) => {
                        if (option.id === ADD_COMPANY_SENTINEL_ID) {
                          return (
                            <li {...props} key="__add_new_company__"
                              style={{ borderTop: "1px solid #e2e8f0", marginTop: 4, paddingTop: 10, paddingBottom: 10, color: "#00838f", fontWeight: 700, display: "flex", alignItems: "center", gap: 8 }}
                              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = alpha("#00acc1", 0.08); }}
                              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = ""; }}>
                              <AddIcon sx={{ fontSize: 16, color: "#00838f" }} />
                              Add New Company
                              {companyPickerInput.trim() && (
                                <Typography component="span" sx={{ ml: 0.5, fontWeight: 600, color: "#64748b", fontSize: 12 }}>
                                  “{companyPickerInput.trim()}”
                                </Typography>
                              )}
                            </li>
                          );
                        }
                        return (<li {...props} key={option.id}>{option.companyName}</li>);
                      }}
                      renderInput={(params) => (
                        <TextField {...params} placeholder="— Pick a company —" sx={signageInputSx} />
                      )}
                      autoHighlight={false}
                      selectOnFocus
                      clearOnBlur={false}
                      handleHomeEndKeys
                    />
                  </Grid>
                )}
                {!isSuperAdmin && currentUser?.company && (
                  <Grid item xs={12}>
                    <Typography sx={{ ...signageLabelSx, mb: 0.5 }}>Company</Typography>
                    <Typography sx={{ fontSize: 13, color: "#475569" }}>{currentUser.company.name}</Typography>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Typography sx={signageLabelSx}>Device Name *</Typography>
                  <TextField
                    fullWidth placeholder="e.g. Lobby TV"
                    value={deviceName}
                    onChange={(e) => setDeviceName(e.target.value)}
                    sx={{ ...signageInputSx, "& .MuiOutlinedInput-root": { ...signageInputSx["& .MuiOutlinedInput-root"], fontSize: 15, fontWeight: 600 } }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography sx={signageLabelSx}>Location</Typography>
                  <TextField fullWidth size="small" placeholder="e.g. Reception, Floor 2"
                    value={location} onChange={(e) => setLocation(e.target.value)} sx={signageInputSx} />
                </Grid>
                <Grid item xs={12}>
                  <Typography sx={signageLabelSx}>Notes</Typography>
                  <TextField fullWidth size="small" multiline rows={3} placeholder="Optional notes about this screen…"
                    value={notes} onChange={(e) => setNotes(e.target.value)} sx={signageInputSx} />
                </Grid>
              </Grid>
            </SignageSection>

            {/* Activation method */}
            <Box sx={{ mt: 3 }}>
              <SignageSection
                icon={<LockOutlinedIcon sx={{ fontSize: 18, color: CARD }} />}
                title="Activation"
                accentColor={CARD}
                subtitle="How this screen becomes active — it stays inactive until activated"
              >
                <RadioGroup value={activationMethod} onChange={(e) => setActivationMethod(e.target.value as ActivationMethod)}>
                  <FormControlLabel value="email" control={<Radio size="small" />} sx={{ alignItems: "flex-start", mb: 0.5 }}
                    label={<Box sx={{ py: 0.5 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Email temporary code</Typography>
                      <Typography sx={{ fontSize: 11.5, color: "#64748b" }}>Send a one-time code the installer enters on the TV.</Typography>
                    </Box>} />
                  <FormControlLabel value="later" control={<Radio size="small" />} sx={{ alignItems: "flex-start", mb: 0.5 }}
                    label={<Box sx={{ py: 0.5 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Activate later</Typography>
                      <Typography sx={{ fontSize: 11.5, color: "#64748b" }}>Create the screen now; send an activation code from its page later.</Typography>
                    </Box>} />
                  <FormControlLabel value="tv_pair" control={<Radio size="small" />} sx={{ alignItems: "flex-start" }}
                    label={<Box sx={{ py: 0.5 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Pair using code displayed on TV</Typography>
                      <Typography sx={{ fontSize: 11.5, color: "#64748b" }}>Enter the pairing code the TV app is showing right now.</Typography>
                    </Box>} />
                </RadioGroup>

                {activationMethod === "email" && (
                  <Box sx={{ mt: 1.5, pl: 0.5 }}>
                    <Typography sx={{ ...signageLabelSx }}>Send activation code to</Typography>
                    <RadioGroup row value={recipient} onChange={(e) => setRecipient(e.target.value as Recipient)} sx={{ mb: 1 }}>
                      <FormControlLabel value="company_email" control={<Radio size="small" />} label={<Typography sx={{ fontSize: 12.5 }}>Registered company email</Typography>} />
                      <FormControlLabel value="my_account" control={<Radio size="small" />} label={<Typography sx={{ fontSize: 12.5 }}>My account email</Typography>} />
                      <FormControlLabel value="custom" control={<Radio size="small" />} label={<Typography sx={{ fontSize: 12.5 }}>Different email address</Typography>} />
                    </RadioGroup>
                    {recipient === "custom" && (
                      <Grid container spacing={1.5}>
                        <Grid item xs={12} sm={6}>
                          <Typography sx={signageLabelSx}>Activation email</Typography>
                          <TextField fullWidth size="small" type="email" placeholder="name@example.com"
                            value={activationEmail} onChange={(e) => setActivationEmail(e.target.value)} sx={signageInputSx} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography sx={signageLabelSx}>Confirm activation email</Typography>
                          <TextField fullWidth size="small" type="email" placeholder="name@example.com"
                            value={activationEmailConfirm} onChange={(e) => setActivationEmailConfirm(e.target.value)}
                            error={activationEmailConfirm.length > 0 && activationEmailConfirm.trim() !== activationEmail.trim()}
                            sx={signageInputSx} />
                        </Grid>
                      </Grid>
                    )}
                  </Box>
                )}

                {activationMethod === "tv_pair" && (
                  <Box sx={{ mt: 1.5 }}>
                    <Typography sx={signageLabelSx}>Pairing code (shown on the TV)</Typography>
                    <TextField fullWidth size="small" placeholder="e.g. K7M9-P4TX"
                      value={pairingCode} onChange={(e) => setPairingCode(e.target.value.toUpperCase())}
                      inputProps={{ style: { letterSpacing: 3, fontFamily: "monospace", fontWeight: 700 } }}
                      sx={signageInputSx} />
                  </Box>
                )}
              </SignageSection>
            </Box>
          </Grid>

          {/* RIGHT COLUMN */}
          <Grid item xs={12} lg={4}>
            <SignageSection
              icon={<SettingsIcon sx={{ fontSize: 18, color: "#00838f" }} />}
              title="Device Details"
              accentColor="#00838f"
              subtitle="Identifier and playlist assignment"
            >
              <Grid container spacing={2.5}>
                <Grid item xs={12}>
                  <Typography sx={signageLabelSx}>Device ID</Typography>
                  <Box sx={{
                    p: 2, borderRadius: 2.5, bgcolor: alpha("#00838f", 0.05),
                    border: "1px dashed #cbd5e1", display: "flex", alignItems: "center", gap: 1,
                  }}>
                    <ShieldOutlinedIcon sx={{ fontSize: 16, color: "#00838f" }} />
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>
                      Generated securely after saving
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: 10, color: "#94a3b8", mt: 0.5 }}>
                    Codes are random, not sequential, and never authenticate a screen on their own.
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography sx={signageLabelSx}>Status</Typography>
                  <Box sx={{
                    p: 2, borderRadius: 2.5, bgcolor: alpha("#f59e0b", 0.06),
                    border: "1px solid #fde68a", display: "flex", alignItems: "center", gap: 1,
                  }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#f59e0b" }} />
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#b45309" }}>
                      Pending activation
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.75 }}>
                    <PlaylistPlayIcon sx={{ fontSize: 14, color: "#00838f" }} />
                    <Typography sx={{ ...signageLabelSx, mb: 0 }}>Assigned Playlist Group</Typography>
                  </Box>
                  <PlaylistGroupSelect
                    value={playlistGroupId}
                    onChange={setPlaylistGroupId}
                    groups={groups}
                    sx={signageInputSx}
                    createInCompanyId={
                      isSuperAdmin ? (companyId === "" ? null : companyId) : (currentUser?.company_id ?? null)
                    }
                  />
                </Grid>
              </Grid>
            </SignageSection>
          </Grid>
        </Grid>
        )}
      </Box>

      <CompanyQuickCreateDialog
        open={addCompanyOpen}
        initialName={companyPickerInput}
        onClose={() => setAddCompanyOpen(false)}
        onCreated={async (created) => {
          try { await refetchCompanies(); } catch { /* non-fatal */ }
          setCompanyId(created.id);
          setCompanyPickerInput(created.companyName);
        }}
      />
    </Box>
  );
};

export default DeviceCreate;
